import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  CategoryListQuery,
  CategoryRow,
  CatalogListQuery,
  ContractQuery,
  ContractRow,
  ContractSaveRequest,
  MaterialPriceQuery,
  MaterialPriceRow,
  SupplierAccountPasswordRequest,
  SupplierAccountPasswordResult,
  SupplierAccountRequest,
  SupplierAccountResult,
  SupplierAccountRow,
  SupplierAdmissionRequest,
  SupplierDetail,
  SupplierListQuery,
  SupplierMaterialQuery,
  SupplierMaterialRow,
  SupplierMaterialSaveRequest,
  SupplierQualSaveRequest,
  SupplierRow,
  SupplierSaveRequest,
  UnitRow
} from '../types/catalog';
import type { PageResult } from '../types/result';

/** 基础资料（只读）：单位 / 品类 / 供应商 / 价格协议。 */
export function createCatalogApi(http: HttpClient) {
  return {
    units(query: CatalogListQuery = {}): Promise<PageResult<UnitRow>> {
      return http.request<PageResult<UnitRow>>({ path: '/wl/unit', query: toQuery(query) });
    },

    categories(query: CategoryListQuery = {}): Promise<CategoryRow[]> {
      return http.request<CategoryRow[]>({ path: '/wl/category', query: toQuery(query) });
    },

    suppliers(query: SupplierListQuery = {}): Promise<PageResult<SupplierRow>> {
      return http.request<PageResult<SupplierRow>>({ path: '/wl/supplier', query: toQuery(query) });
    },

    supplier(id: Id): Promise<SupplierDetail> {
      return http.request<SupplierDetail>({ path: `/wl/supplier/${id}` });
    },

    /**
     * 供应商门户「我的企业档案」：按令牌绑定的供应商主体解析，不接受客户端指定 id。
     * 账号未绑定供应商时 403——不要退化成用 `profile.orgId` 当供应商 id。
     */
    supplierMe(): Promise<SupplierDetail> {
      return http.request<SupplierDetail>({ path: '/wl/supplier/me' });
    },

    /**
     * 供应商 SKU 映射（`wl_supplier_material`）：供应商自有编码/条码/型号 ↔ 平台物资。
     * 判源、供货、结算均按 `materialId` 归一，映射缺失会导致对码失败。
     */
    supplierMaterials(supplierId: Id, query: SupplierMaterialQuery = {}): Promise<PageResult<SupplierMaterialRow>> {
      return http.request<PageResult<SupplierMaterialRow>>({
        path: `/wl/supplier/${supplierId}/material`,
        query: toQuery(query)
      });
    },

    /** 新增或更新映射（同一 supplier+material 覆盖）。 */
    saveSupplierMaterial(supplierId: Id, body: SupplierMaterialSaveRequest): Promise<SupplierMaterialRow> {
      return http.request<SupplierMaterialRow>({
        path: `/wl/supplier/${supplierId}/material`,
        method: 'POST',
        body
      });
    },

    removeSupplierMaterial(id: Id): Promise<{ id: Id; deleted: boolean }> {
      return http.request<{ id: Id; deleted: boolean }>({
        path: `/wl/supplier-material/${id}`,
        method: 'DELETE'
      });
    },

    prices(query: MaterialPriceQuery = {}): Promise<MaterialPriceRow[]> {
      return http.request<MaterialPriceRow[]>({ path: '/wl/material-price', query: toQuery(query) });
    },

    /** 供应商建档 / 编辑（运营端准入前置）。 */
    saveSupplier(body: SupplierSaveRequest): Promise<{ id: Id; supplierCode?: string; created?: boolean }> {
      return http.request({ path: '/wl/supplier', method: 'POST', body });
    },

    /**
     * 供应商门户账号开户：账号由服务端按「机构号 + 4 位序号」生成，
     * 同时写入 `sys_user.supplier_id` 并自动授 `SUPPLIER` 角色（DR-8.14）。
     */
    openSupplierAccount(id: Id, body: SupplierAccountRequest): Promise<SupplierAccountResult> {
      return http.request<SupplierAccountResult>({
        path: `/wl/supplier/${id}/account`,
        method: 'POST',
        body
      });
    },

    /** 供应商门户账号列表（只返回挂在该供应商名下、且属于本租户的账号）。 */
    supplierAccounts(id: Id): Promise<SupplierAccountRow[]> {
      return http.request<SupplierAccountRow[]>({ path: `/wl/supplier/${id}/accounts` });
    },

    /** 重置供应商门户账号口令；`password` 留空由服务端生成一次性口令并回显。 */
    resetSupplierAccountPassword(
      id: Id,
      userId: Id,
      body: SupplierAccountPasswordRequest = {}
    ): Promise<SupplierAccountPasswordResult> {
      return http.request<SupplierAccountPasswordResult>({
        path: `/wl/supplier/${id}/accounts/${userId}/password`,
        method: 'POST',
        body
      });
    },

    /** 供应商门户账号启停。 */
    setSupplierAccountStatus(
      id: Id,
      userId: Id,
      status: 'ENABLED' | 'DISABLED'
    ): Promise<{ id: Id; status: string }> {
      return http.request<{ id: Id; status: string }>({
        path: `/wl/supplier/${id}/accounts/${userId}/status`,
        method: 'POST',
        body: { status }
      });
    },

    /** 解绑：清 `supplier_id` 并停用，账号随即失去供应商门户权限（fail-closed）。 */
    unbindSupplierAccount(id: Id, userId: Id): Promise<{ id: Id; unbound: boolean }> {
      return http.request<{ id: Id; unbound: boolean }>({
        path: `/wl/supplier/${id}/accounts/${userId}/unbind`,
        method: 'POST'
      });
    },

    /** 准入状态机：TRIAL / ACTIVE / STOPPED / BLACKLIST（黑名单必填原因）。 */
    admitSupplier(id: Id, body: SupplierAdmissionRequest): Promise<{ id: Id; cooperateStatus: string }> {
      return http.request<{ id: Id; cooperateStatus: string }>({
        path: `/wl/supplier/${id}/admission`,
        method: 'POST',
        body
      });
    },

    quals(supplierId: Id): Promise<SupplierQualLike[]> {
      return http.request<SupplierQualLike[]>({ path: `/wl/supplier/${supplierId}/qual` });
    },

    saveQual(supplierId: Id, body: SupplierQualSaveRequest): Promise<{ id: Id; created?: boolean }> {
      return http.request({ path: `/wl/supplier/${supplierId}/qual`, method: 'POST', body });
    },

    removeQual(id: Id): Promise<{ id: Id; deleted: boolean }> {
      return http.request<{ id: Id; deleted: boolean }>({ path: `/wl/supplier/qual/${id}`, method: 'DELETE' });
    },

    /** 合同台账（统招框架 / 分签 / 零星）。 */
    contracts: {
      list(query: ContractQuery = {}): Promise<PageResult<ContractRow>> {
        return http.request<PageResult<ContractRow>>({ path: '/wl/contract', query: toQuery(query) });
      },

      save(body: ContractSaveRequest): Promise<{ id: Id; contractNo?: string; created?: boolean }> {
        return http.request({ path: '/wl/contract', method: 'POST', body });
      }
    }
  };
}

/** 与 `SupplierQualRow` 同构（避免循环引用，仅用于返回值）。 */
type SupplierQualLike = {
  id: string;
  supplierId: string;
  qualType: string | null;
  qualNo: string | null;
  fileUrl: string | null;
  issueAt: string | null;
  expireAt: string | null;
  remindDays: number;
  status: string | null;
};

export type CatalogApi = ReturnType<typeof createCatalogApi>;
